import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const sunsetHorizon: TemplateRegistration = {
  meta: {
    id: 'sunset-horizon',
    name: 'Sunset Horizon',
    category: 'Modern',
    description: 'Warm sunset gradients with asymmetric content blocks',
    previewImage: 'https://images.unsplash.com/photo-1495616811223-4d98c6e9c869?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const heroImage = photos[0] || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Josefin Sans', 'Source Sans 3']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      const aboutImg = photos[1] || null;
      sections.push(`
      <section id="about" class="section section-dark">
        <div class="container">
          <div class="gradient-bar"></div>
          <h2 class="section-title">About Us</h2>
          <div class="about-grid">
            <p class="about-text">${safeAbout}</p>
            <div class="about-image">
              ${aboutImg ? `<img src="${escapeAttr(aboutImg)}" alt="${safeName}" loading="lazy">` : `<div class="about-no-img"></div>`}
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceCards = data.services.map((s) => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-card">
            <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
            <h3>${sName}</h3>
            <p>${sDesc}</p>
            ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-darker">
        <div class="container">
          <h2 class="section-title">Our Services</h2>
          <div class="services-grid">${serviceCards}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`).join('');
      sections.push(`
      <section id="gallery" class="section section-dark">
        <div class="container">
          <h2 class="section-title">Gallery</h2>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map((t) => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <div class="testimonial-body">
              <p>&ldquo;${tContent}&rdquo;</p>
            </div>
            <div class="testimonial-author">
              <strong>${tName}</strong>
              ${tRole ? `<span>${tRole}</span>` : ''}
              ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-darker">
        <div class="container">
          <h2 class="section-title">What People Say</h2>
          <div class="testimonials-grid">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section section-dark">
        <div class="container">
          <h2 class="section-title">FAQ</h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-darker">
        <div class="container">
          <h2 class="section-title">Pricing</h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section section-dark">
        <div class="container">
          <h2 class="section-title">Hours</h2>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-darker">
        <div class="container">
          <h2 class="section-title">Get In Touch</h2>
          <div class="contact-grid">
            <div class="contact-info">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>` : ''}
            </div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Your Name" required>
              <input type="email" placeholder="Your Email" required>
              <textarea placeholder="Message" rows="4" required></textarea>
              <button type="submit">Send Message</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --primary: #FF6B6B; --bg: #2C3E50; --bg-alt: #1A252F; --accent: #FF6B6B;
      --text: #ECF0F1; --text-muted: #95A5A6;
      --font-heading: 'Josefin Sans', sans-serif; --font-body: 'Source Sans 3', sans-serif;
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.6; }
    .container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--accent); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .8; }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(44,62,80,.94); backdrop-filter: blur(10px); border-bottom: 3px solid transparent; border-image: linear-gradient(90deg, var(--accent), #F39C12) 1; }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; max-width: 1200px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.5rem; color: var(--text); font-weight: 700; letter-spacing: 1px; }
    .nav-links { display: flex; gap: 28px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .85rem; text-transform: uppercase; letter-spacing: 1.5px; font-weight: 600; }
    .nav-links a:hover { color: var(--accent); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 28px; height: 3px; background: var(--accent); margin: 5px 0; transition: .3s; }

    /* HERO - photo collage mosaic */
    .hero { position: relative; min-height: 100vh; overflow: hidden; }
    .hero-collage { display: grid; grid-template-columns: 1fr 1fr 1fr; grid-template-rows: 1fr 1fr; min-height: 100vh; }
    .hero-collage-img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .hero-collage-cell { overflow: hidden; position: relative; }
    .hero-collage-cell:first-child { grid-row: 1 / 3; }
    .hero-collage-cell .hero-no-img { width: 100%; height: 100%; background: linear-gradient(135deg, rgba(44,62,80,1), rgba(255,107,107,.4)); }
    .hero-text-panel { position: absolute; bottom: 0; left: 0; right: 0; z-index: 3; background: rgba(44,62,80,.82); backdrop-filter: blur(6px); padding: 48px 24px 40px; }
    .hero-text-inner { max-width: 1200px; margin: 0 auto; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2.8rem, 7vw, 5rem); line-height: 1.1; margin-bottom: 16px; font-weight: 700; }
    .hero h1 .gradient-text { background: linear-gradient(135deg, var(--accent), #F39C12); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
    .hero-sub { font-size: clamp(1rem, 2vw, 1.2rem); color: var(--text-muted); max-width: 560px; margin-bottom: 32px; }
    .hero-cta { display: inline-block; padding: 16px 40px; background: linear-gradient(135deg, var(--accent), #F39C12); color: #fff; font-family: var(--font-heading); font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; border: none; border-radius: 4px; cursor: pointer; transition: transform .2s, box-shadow .2s; }
    .hero-cta:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(255,107,107,.35); opacity: 1; color: #fff; }

    /* DIAGONAL DIVIDER */
    .section { padding: 80px 0; position: relative; }
    .section-dark { background: var(--bg); }
    .section-darker { background: var(--bg-alt); }
    .section-darker::before { content: ''; position: absolute; top: -40px; left: 0; right: 0; height: 40px; background: var(--bg-alt); clip-path: polygon(0 100%, 100% 0, 100% 100%); }
    .section-dark + .section-darker::before, .section-darker + .section-dark::before { display: block; }
    .section-title { font-family: var(--font-heading); font-size: clamp(1.8rem, 4vw, 2.8rem); font-weight: 700; margin-bottom: 48px; }
    .gradient-bar { width: 80px; height: 4px; background: linear-gradient(90deg, var(--accent), #F39C12); margin-bottom: 20px; border-radius: 2px; }

    /* ABOUT - two-col text + image */
    .about-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: center; }
    .about-text { font-size: 1.1rem; color: var(--text-muted); max-width: 800px; line-height: 1.8; }
    .about-image img { width: 100%; height: 320px; object-fit: cover; border-radius: 8px; }
    .about-image .about-no-img { width: 100%; height: 320px; background: linear-gradient(135deg, var(--accent), #F39C12); border-radius: 8px; opacity: .15; }

    /* SERVICES - 3-col grid */
    .services-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; }
    .service-card { background: var(--bg); border: 1px solid rgba(255,107,107,.15); border-radius: 8px; padding: 36px 28px; position: relative; overflow: hidden; transition: border-color .3s, transform .3s; }
    .service-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, var(--accent), #F39C12); opacity: 0; transition: opacity .3s; }
    .service-card:hover { border-color: var(--accent); transform: translateY(-4px); }
    .service-card:hover::before { opacity: 1; }
    .service-icon { color: var(--accent); margin-bottom: 14px; }
    .icon-char { font-size: 28px; line-height: 1; }
    .service-card h3 { font-family: var(--font-heading); font-size: 1.2rem; font-weight: 700; margin-bottom: 10px; }
    .service-card p { color: var(--text-muted); font-size: .95rem; line-height: 1.6; }
    .service-price { display: inline-block; margin-top: 14px; color: var(--accent); font-weight: 700; font-size: 1.1rem; }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
    .gallery-item img { width: 100%; height: 240px; object-fit: cover; display: block; border-radius: 6px; transition: transform .3s; }
    .gallery-item img:hover { transform: scale(1.03); }

    /* TESTIMONIALS - 2-col grid */
    .testimonials-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 32px; }
    .testimonial-card { padding: 32px; background: var(--bg); border-radius: 8px; border-left: 4px solid var(--accent); transition: transform .3s; }
    .testimonial-card:hover { transform: translateY(-4px); }
    .testimonial-body p { font-size: 1.05rem; font-style: italic; color: var(--text-muted); line-height: 1.7; margin-bottom: 20px; }
    .testimonial-author strong { display: block; color: var(--text); font-size: 1rem; }
    .testimonial-author span { color: var(--text-muted); font-size: .85rem; }
    .testimonial-stars { margin-top: 8px; }
    .star { color: #57534e; font-size: 1rem; }
    .star.filled { color: var(--accent); }

    /* FAQ */
    .faq-list { max-width: 800px; }
    .faq-item { border-bottom: 1px solid rgba(255,107,107,.15); padding: 24px 0; }
    .faq-q { font-family: var(--font-heading); font-size: 1.15rem; font-weight: 700; margin-bottom: 12px; }
    .faq-a { color: var(--text-muted); line-height: 1.7; }

    /* PRICE LIST */
    .price-list { max-width: 800px; }
    .price-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 24px 0; border-bottom: 1px solid rgba(255,107,107,.15); gap: 24px; }
    .price-info h3 { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; margin-bottom: 4px; }
    .price-info p { color: var(--text-muted); font-size: .9rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.5rem; color: var(--accent); white-space: nowrap; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid rgba(255,107,107,.1); color: var(--text-muted); font-size: .95rem; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: linear-gradient(90deg, var(--accent), #F39C12); padding: 16px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: #fff; font-size: 1.2rem; }
    .review-text { color: #fff; font-weight: 700; font-size: .95rem; }

    /* PROMO */
    .promo-banner { background: var(--bg-alt); padding: 20px 0; border-bottom: 1px solid rgba(255,107,107,.2); }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
    .promo-badge { background: linear-gradient(135deg, var(--accent), #F39C12); color: #fff; font-family: var(--font-heading); padding: 8px 20px; font-size: 1rem; font-weight: 700; border-radius: 4px; }
    .promo-content strong { display: block; margin-bottom: 2px; }
    .promo-content span { color: var(--text-muted); font-size: .9rem; }

    /* CONTACT */
    .contact-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: start; }
    .contact-info { display: flex; flex-direction: column; gap: 20px; }
    .contact-item { display: flex; align-items: center; gap: 12px; color: var(--text-muted); }
    .contact-item .icon-char { color: var(--accent); flex-shrink: 0; font-size: 20px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { display: inline-block; margin-top: 8px; font-weight: 600; text-transform: uppercase; font-size: .85rem; letter-spacing: 1px; }
    .contact-form { display: flex; flex-direction: column; gap: 16px; }
    .contact-form input, .contact-form textarea { background: rgba(255,255,255,.06); border: 1px solid rgba(255,107,107,.2); border-radius: 6px; color: var(--text); padding: 14px 16px; font-family: var(--font-body); font-size: .95rem; outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--accent); }
    .contact-form button { padding: 16px; background: linear-gradient(135deg, var(--accent), #F39C12); color: #fff; font-family: var(--font-heading); font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; border: none; border-radius: 6px; cursor: pointer; transition: transform .2s; }
    .contact-form button:hover { transform: translateY(-2px); }

    /* FOOTER */
    .footer { background: var(--bg-alt); border-top: 3px solid transparent; border-image: linear-gradient(90deg, var(--accent), #F39C12) 1; padding: 40px 0; text-align: center; }
    .footer p { color: var(--text-muted); font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(44,62,80,.98); padding: 16px 24px; gap: 16px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .about-grid { grid-template-columns: 1fr; }
      .hero-collage { grid-template-columns: 1fr 1fr; }
      .hero-collage-cell:first-child { grid-row: auto; }
      .services-grid { grid-template-columns: 1fr; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .contact-grid { grid-template-columns: 1fr; }
      .testimonials-grid { grid-template-columns: 1fr; }
      .price-row { flex-direction: column; gap: 8px; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero-content { padding: 100px 16px 60px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-collage">
      <div class="hero-collage-cell">${heroImage ? `<img class="hero-collage-img" src="${escapeAttr(heroImage)}" alt="${safeName}">` : `<div class="hero-no-img"></div>`}</div>
      <div class="hero-collage-cell">${photos[1] ? `<img class="hero-collage-img" src="${escapeAttr(photos[1])}" alt="${safeName}">` : `<div class="hero-no-img"></div>`}</div>
      <div class="hero-collage-cell">${photos[2] ? `<img class="hero-collage-img" src="${escapeAttr(photos[2])}" alt="${safeName}">` : `<div class="hero-no-img"></div>`}</div>
      <div class="hero-collage-cell">${photos[3] ? `<img class="hero-collage-img" src="${escapeAttr(photos[3])}" alt="${safeName}">` : `<div class="hero-no-img"></div>`}</div>
    </div>
    <div class="hero-text-panel">
      <div class="hero-text-inner">
        <h1><span class="gradient-text">${safeHeadline}</span></h1>
        <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
        ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get Started</a>` : ''}
      </div>
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
